/*  src/pages/UsersTable.jsx  */
import React, { useEffect, useMemo, useState } from 'react';
import API from '../services/api';
import Sidebar   from '../components/AnalyticsSidebar';
import DataTable from '../components/DataTable';
import './_analytics.css';

const ALLOWED_ROLES = ['student','warden','gatekeeper','vc-office','guest-house','admin','superadmin'];
const HOSTEL_TYPES  = ['hostler','non-hostler'];

const digitsOnly = (v) => String(v || '').replace(/[^\d]/g, '');

export default function UsersTable() {
  const [allUsers, setAllUsers] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [role, setRole]         = useState('all');            // filter: role
  const [status, setStatus]     = useState('all');            // filter: verified | unverified | all
  const [hostelFilter, setHostelFilter] = useState('all');    // filter: hostel type (students)
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Checkbox selection for bulk delete
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Progress tracking for bulk upload
  const [uploadProgress, setUploadProgress] = useState({ show: false, current: 0, total: 0, percentage: 0 });

  /* ───────── fetch ───────── */
  useEffect(() => {
    (async () => {
      try {
        const [usersRes, hostelsRes] = await Promise.all([
          API.get('/users'),
          API.get('/hostels?isActive=true')
        ]);
        setAllUsers(usersRes.data || []);
        setHostels(hostelsRes.data || []);
      } catch (e) {
        alert(e.response?.data?.msg || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshKey]);

  /* ───────── derived rows after filters ───────── */
  const rows = useMemo(() => {
    return allUsers.filter(u => {
      if (role !== 'all'   && u.role !== role) return false;
      if (status !== 'all' && (status === 'verified' ? !u.verified : u.verified)) return false;
      if (hostelFilter !== 'all' && u.role === 'student' && u.hostelType !== hostelFilter) return false;
      return true;
    });
  }, [allUsers, role, status, hostelFilter]);

  /* ───────── actions ───────── */
  const verifyUser = async (id) => {
    if (!window.confirm('Mark this account as VERIFIED?')) return;
    try {
      await API.patch(`/users/${id}/verify`);
      setRefreshKey(k => k + 1);
    } catch (e) {
      alert(e.response?.data?.msg || e.message);
    }
  };

  const resetPassword = async (id) => {
    if (!window.confirm('Reset this user’s password? A temporary password will be emailed.')) return;
    try {
      await API.post(`/users/${id}/reset-password`);
      alert('Temporary password emailed to the user.');
    } catch (e) {
      alert(e.response?.data?.msg || e.message);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user account? This cannot be undone.')) return;
    try {
      await API.delete(`/users/${id}`);
      setAllUsers(list => list.filter(u => u._id !== id));
    } catch (e) {
      alert(e.response?.data?.msg || e.message);
    }
  };

  const bulkDeleteUsers = async () => {
    if (!selectedUsers.length) {
      setBulkResult({ type: 'error', message: 'No users selected for deletion', details: {} });
      return;
    }
    if (!window.confirm(`Delete ${selectedUsers.length} user(s)? This cannot be undone.`)) return;
    try {
      const { data } = await API.post('/users/bulk-delete', { ids: selectedUsers });
      setBulkResult({ 
        type: 'success', 
        message: `Successfully deleted ${data.deleted} user(s)`, 
        details: { deleted: data.deleted, failed: data.failed || 0 } 
      });
      setSelectedUsers([]);
      setRefreshKey(k => k + 1);
    } catch (e) {
      setBulkResult({ 
        type: 'error', 
        message: e.response?.data?.msg || e.message, 
        details: {} 
      });
    }
  };

  /* ───────── add (single) ───────── */
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '', email: '', role: 'student', studentId: '',
    hostelType: 'hostler',
    hostel: '',
    affidavit: false,
    password: '',
    guardian_contact: '',
    guardians_info: [{ name:'', relation:'', contact:'' }]
  });

  const addGuardianRow = () => {
    setAddForm(f => ({
      ...f,
      guardians_info: f.guardians_info.length >= 5 ? f.guardians_info : [...f.guardians_info, { name:'', relation:'', contact:'' }]
    }));
  };
  const removeGuardianRow = (idx) => {
    setAddForm(f => ({ ...f, guardians_info: f.guardians_info.filter((_,i)=>i!==idx) }));
  };

  const submitAdd = async () => {
    try {
      const payload = {
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        role: addForm.role,
        password: addForm.password.trim() || undefined,
        studentId: addForm.role === 'student' ? addForm.studentId.trim() : undefined,
        hostelType: addForm.role === 'student' ? addForm.hostelType : undefined,
        hostel: addForm.role === 'student' ? (addForm.hostel || undefined) : undefined,
        affidavit: addForm.role === 'student' ? addForm.affidavit : undefined,
        guardian_contact: addForm.role === 'student' ? digitsOnly(addForm.guardian_contact) || undefined : undefined,
        guardians_info: (addForm.guardians_info||[])
          .map(g=>({ name:g.name?.trim(), relation:g.relation?.trim(), contact:digitsOnly(g.contact) }))
          .filter(g=>g.name || g.contact) // only non-empty rows
      };
      if (!payload.name || !payload.email || !ALLOWED_ROLES.includes(payload.role)) {
        alert('Please provide name, email and a valid role.');
        return;
      }
      if (payload.role === 'student') {
        if (!payload.studentId) return alert('Student ID is required for role "student".');
        if (!HOSTEL_TYPES.includes(payload.hostelType)) return alert('Select Hostler/Non-Hostler.');
      }
      await API.post('/users', payload); // server auto-verifies & emails temp password
      setAddOpen(false);
      setAddForm({
        name:'', email:'', role:'student', studentId:'',
        hostelType:'hostler',
        hostel: '',
        affidavit: false,
        password: '',
        guardian_contact:'',
        guardians_info:[{name:'',relation:'',contact:''}]
      });
      setRefreshKey(k=>k+1);
    } catch (e) {
      alert(e.response?.data?.msg || e.message);
    }
  };

  /* ───────── edit (single) ───────── */
  const [edit, setEdit] = useState(null); // holds user object being edited

  const saveEdit = async () => {
    try {
      const payload = {
        name: edit.name,
        email: edit.email,
        role: edit.role,
        studentId: edit.role === 'student' ? (edit.studentId || '').trim() : undefined,
        hostelType: edit.role === 'student' ? edit.hostelType : undefined,
        hostel: edit.role === 'student' ? (edit.hostel || undefined) : undefined,
        affidavit: edit.role === 'student' ? edit.affidavit : undefined,
        guardian_contact: edit.role === 'student' ? (digitsOnly(edit.guardian_contact) || undefined) : undefined,
        guardians_info: (edit.guardians_info||[])
          .map(g=>({ name:(g.name||'').trim(), relation:(g.relation||'').trim(), contact:digitsOnly(g.contact) }))
          .filter(g=>g.name || g.contact),
      };
      await API.put(`/users/${edit._id}`, payload);
      setEdit(null);
      setRefreshKey(k => k + 1);
    } catch (e) {
      alert(e.response?.data?.msg || e.message);
    }
  };

  /* ───────── bulk upload (via CSV file) ───────── */
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkInfo, setBulkInfo] = useState({ rows: 0, valid: 0, invalid: 0, errors: [] });
  const [bulkPresets, setBulkPresets] = useState({ hostelType: 'hostler', hostel: '' });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { type: 'success'|'error', message: '', details: {} }

  // Header contract (simplified - hostelType and hostel set in modal)
  const TEMPLATE_HEADERS = [
    'name','email','studentId','password','affidavit','guardian_contact',
    'guardian1_name','guardian1_relation','guardian1_contact',
    'guardian2_name','guardian2_relation','guardian2_contact',
    'guardian3_name','guardian3_relation','guardian3_contact',
    'guardian4_name','guardian4_relation','guardian4_contact',
    'guardian5_name','guardian5_relation','guardian5_contact'
  ];

  const SAMPLE_ROW = [
    'Ali Khan','ali.khan@bnbwu.edu.pk','21SW001','','true','923001234567',
    'Abid Khan','Father','923001234567',
    'Nadia Khan','Mother','923451234567',
    'Hamza Khan','Brother','923331234567',
    'Sara Ahmed','Guardian','923211234567',
    '','',''
  ];

  const downloadTemplate = () => {
    const lines = [
      TEMPLATE_HEADERS.join(','),
      SAMPLE_ROW.join(','),
    ];
    const csv = lines.join('\n') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'users_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Robust CSV line splitter (supports quotes)
  const splitCSVLine = (line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        out.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out;
  };

  const parseCSV = (text) => {
    const lines = text.replace(/\r/g, '').split('\n').filter(l => l.trim().length > 0);
    if (!lines.length) return { headers: [], rows: [] };

    const headers = splitCSVLine(lines[0]).map(h => h.trim());
    const rows = lines.slice(1).map(line => splitCSVLine(line));

    return { headers, rows };
  };

  const validateHeaders = (headers) => {
    const missing = TEMPLATE_HEADERS.filter(h => !headers.includes(h));
    const extra   = headers.filter(h => !TEMPLATE_HEADERS.includes(h));
    return { missing, extra };
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setBulkFile(file || null);
    setBulkInfo({ rows: 0, valid: 0, invalid: 0, errors: [] });
  };

  const loadAndParseCSV = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || ''));
      fr.onerror = reject;
      fr.readAsText(file, 'utf-8');
    });

  const submitBulk = async () => {
    try {
      if (!bulkFile) { 
        setBulkResult({ type: 'error', message: 'Please choose a CSV file.', details: {} });
        return; 
      }
      if (!bulkPresets.hostelType) { 
        setBulkResult({ type: 'error', message: 'Please select Student Type (Hostler/Non-Hostler)', details: {} });
        return; 
      }
      
      setBulkLoading(true);
      const content = await loadAndParseCSV(bulkFile);
      const { headers, rows } = parseCSV(content);

      // Validate headers strictly
      const { missing, extra } = validateHeaders(headers);
      if (missing.length) {
        setBulkResult({ 
          type: 'error', 
          message: 'CSV is missing required columns', 
          details: { missing } 
        });
        setBulkLoading(false);
        return;
      }
      if (extra.length) {
        // Just log warning, don't block
        console.warn('CSV has unexpected columns:', extra);
      }

      const hIndex = Object.fromEntries(headers.map((h, i) => [h, i]));
      let valid = 0, invalid = 0;
      const errors = [];
      const users = rows.map((cols, rowIdx) => {
        const val = (key) => (cols[hIndex[key]] ?? '').trim();

        const name  = val('name');
        const email = val('email');
        const role  = 'student'; // All bulk uploads are students
        const password = val('password');
        const studentId = val('studentId');
        const affidavit = val('affidavit').toLowerCase() === 'true';
        const guardian_contact = digitsOnly(val('guardian_contact'));

        const guardians = [];
        for (let i = 1; i <= 5; i++) {
          const gn = val(`guardian${i}_name`);
          const gr = val(`guardian${i}_relation`);
          const gc = digitsOnly(val(`guardian${i}_contact`));
          if (gn || gc) guardians.push({ name: gn, relation: gr, contact: gc });
        }

        const obj = {
          name, email, role,
          password: password || undefined,
          studentId: studentId || undefined,
          hostelType: bulkPresets.hostelType, // From modal
          hostel: bulkPresets.hostel || undefined, // From modal
          affidavit: affidavit,
          guardian_contact: guardian_contact || undefined,
          guardians_info: guardians
        };

        const studOk = !!studentId;
        const ok = !!name && !!email && studOk;
        
        if (ok) valid++; else {
          invalid++;
          errors.push(`Row ${rowIdx + 2}: missing name, email, or studentId`);
        }
        return ok ? obj : null;
      }).filter(Boolean);

      setBulkInfo({ rows: rows.length, valid, invalid, errors });

      if (!users.length) {
        setBulkResult({ 
          type: 'error', 
          message: 'No valid rows to upload', 
          details: { total: rows.length, invalid, errors } 
        });
        setBulkLoading(false);
        return;
      }

      // Show progress modal
      setUploadProgress({ show: true, current: 0, total: users.length, percentage: 0 });
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress(p => {
          if (p.current >= p.total) return p;
          const newCurrent = Math.min(p.current + Math.ceil(p.total / 10), p.total);
          return {
            ...p,
            current: newCurrent,
            percentage: Math.floor((newCurrent / p.total) * 100)
          };
        });
      }, 300);

      const { data } = await API.post('/users/bulk', { users });
      clearInterval(progressInterval);
      setUploadProgress({ show: false, current: 0, total: 0, percentage: 0 });
      setBulkResult({ 
        type: 'success', 
        message: 'Bulk upload completed successfully!', 
        details: { 
          created: data.created?.length || 0, 
          skipped: data.skipped?.length || 0,
          skippedList: data.skipped || []
        } 
      });
      setBulkOpen(false);
      setBulkFile(null);
      setBulkInfo({ rows: 0, valid: 0, invalid: 0, errors: [] });
      setBulkPresets({ hostelType: 'hostler', hostel: '' });
      setRefreshKey(k => k + 1);
    } catch (e) {
      setBulkResult({ 
        type: 'error', 
        message: e.response?.data?.msg || e.message, 
        details: {} 
      });
    } finally {
      setBulkLoading(false);
    }
  };

  /* table columns */
  const toggleSelectAll = () => {
    if (selectedUsers.length === rows.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(rows.map(u => u._id));
    }
  };
  
  const toggleSelectUser = (id) => {
    setSelectedUsers(prev => 
      prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]
    );
  };

  const columns = [
    { 
      header: (
        <input 
          type="checkbox" 
          checked={selectedUsers.length === rows.length && rows.length > 0}
          onChange={toggleSelectAll}
          style={{cursor:'pointer'}}
        />
      ), 
      cell: u => (
        <input 
          type="checkbox" 
          checked={selectedUsers.includes(u._id)}
          onChange={() => toggleSelectUser(u._id)}
          style={{cursor:'pointer'}}
        />
      )
    },
    { header: 'Name',  cell: u => u.name },
    { header: 'Email', cell: u => u.email },
    { header: 'Role',  cell: u => u.role },
    { header: 'Student ID', cell: u => u.studentId || '—' },
    { header: 'Hostel Type', cell: u => (u.role === 'student' ? (u.hostelType || '—') : '—') },
    { header: 'Affidavit', cell: u => (u.role === 'student' ? (
        <span className={`status-badge ${u.affidavit ? 'status-approved' : 'status-pending'}`}>
          {u.affidavit ? 'Yes' : 'No'}
        </span>
      ) : '—')
    },
    { header: 'Guardian Contact', cell: u => (u.role === 'student' ? (u.guardian_contact || '—') : '—') },
    { header: 'Guardians', cell: u => (u.guardians_info?.length ? `${u.guardians_info.length} saved` : '—') },
    { header: 'Verified', cell: u => (
        <span className={`status-badge ${u.verified ? 'status-approved' : 'status-pending'}`}>
          {u.verified ? 'Yes' : 'No'}
        </span>
      )
    },
    { header: 'Action', cell: u => (
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button
            className="action-btn green"
            onClick={() => verifyUser(u._id)}
            disabled={u.verified}
            title={u.verified ? 'Already verified' : 'Mark verified'}
          >
            ✓ Verify
          </button>
          <button className="action-btn" onClick={() => resetPassword(u._id)}>↻ Reset PW</button>
          <button className="action-btn blue" onClick={() => setEdit(u)}>✎ Edit</button>
          <button className="action-btn red"  onClick={() => deleteUser(u._id)}>🗑 Delete</button>
        </div>
      )
    },
  ];

  return (
    <div className="split-layout">
      <Sidebar />

      <div className="table-wrapper">
        <div className="table-header-row">
          <div>
            <h2>👥 Users</h2>
            {selectedUsers.length > 0 && (
              <span style={{fontSize:13, color:'#6b7280', marginLeft:8}}>
                ({selectedUsers.length} selected)
              </span>
            )}
          </div>
          <div className="btn-row">
            {selectedUsers.length > 0 && (
              <button className="action-btn red" onClick={bulkDeleteUsers}>
                🗑 Delete Selected ({selectedUsers.length})
              </button>
            )}
            <button className="action-btn" onClick={downloadTemplate}>⇩ CSV Template</button>
            <button className="action-btn" onClick={()=>setBulkOpen(true)}>⬆ Bulk Upload</button>
            <button className="action-btn blue" onClick={()=>setAddOpen(true)}>＋ Add User</button>
          </div>
        </div>

        {/* Filters row (role + status + hostel type) */}
        <div className="search-filter" style={{gap:12}}>
          <div>
            <label style={{fontSize:12,display:'block',opacity:0.7}}>Role</label>
            <select value={role} onChange={e=>setRole(e.target.value)}>
              <option value="all">All</option>
              {ALLOWED_ROLES.map(r=> <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <label style={{fontSize:12,display:'block',opacity:0.7}}>Status</label>
            <select value={status} onChange={e=>setStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>

          <div>
            <label style={{fontSize:12,display:'block',opacity:0.7}}>Hostel Type</label>
            <select value={hostelFilter} onChange={e=>setHostelFilter(e.target.value)}>
              <option value="all">All</option>
              {HOSTEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* DataTable has built-in free-text search; include hostelType too */}
        <DataTable
          columns={columns}
          rows={rows}
          searchKeys={[ 'name', 'email', 'studentId', 'hostelType' ]}
        />

        {/* Add modal */}
        {addOpen && (
          <div className="modal-overlay">
            <div className="modal-card">
              <h3>Add User</h3>
              <p style={{marginTop:4,opacity:.7}}>Users added by admin are auto-verified and emailed a temporary password.</p>
              <div className="form-grid">
                <label>
                  <span>Name</span>
                  <input value={addForm.name} onChange={e=>setAddForm({ ...addForm, name:e.target.value })} />
                </label>
                <label>
                  <span>Email</span>
                  <input type="email" value={addForm.email}
                         onChange={e=>setAddForm({ ...addForm, email:e.target.value })} />
                </label>
                <label>
                  <span>Role</span>
                  <select value={addForm.role} onChange={e=>setAddForm({ ...addForm, role:e.target.value })}>
                    {ALLOWED_ROLES.map(r=> <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label>
                  <span>Password (optional - leave blank for auto-generate)</span>
                  <input type="text" placeholder="Auto-generated if empty" value={addForm.password}
                         onChange={e=>setAddForm({ ...addForm, password:e.target.value })} />
                </label>

                {addForm.role === 'student' && (
                  <>
                    <label>
                      <span>Student ID</span>
                      <input value={addForm.studentId}
                             onChange={e=>setAddForm({ ...addForm, studentId:e.target.value })} />
                    </label>
                    <label>
                      <span>Hostel Type</span>
                      <select value={addForm.hostelType}
                              onChange={e=>setAddForm({ ...addForm, hostelType:e.target.value })}>
                        {HOSTEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>Hostel</span>
                      <select value={addForm.hostel}
                              onChange={e=>setAddForm({ ...addForm, hostel:e.target.value })}>
                        <option value="">-- Select Hostel (Optional) --</option>
                        {hostels
                          .filter(h => h.hostelType === addForm.hostelType && h.isActive)
                          .map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
                      </select>
                    </label>
                    <label style={{display:'flex',alignItems:'center',gap:8}}>
                      <input type="checkbox" checked={addForm.affidavit}
                             onChange={e=>setAddForm({ ...addForm, affidavit:e.target.checked })} />
                      <span>Affidavit Submitted</span>
                    </label>
                    <label>
                      <span>Primary Guardian Contact (SMS)</span>
                      <input placeholder="923001234567" value={addForm.guardian_contact}
                             onChange={e=>setAddForm({ ...addForm, guardian_contact: digitsOnly(e.target.value) })} />
                    </label>
                  </>
                )}
              </div>

              <h4 style={{marginTop:10}}>Guardians (up to 5)</h4>
              {addForm.guardians_info.map((g,idx)=> (
                <div key={idx} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr auto',gap:8,marginTop:6}}>
                  <input placeholder="Name" value={g.name}
                         onChange={e=>{
                           const gi=[...addForm.guardians_info]; gi[idx]={...gi[idx], name:e.target.value};
                           setAddForm({...addForm, guardians_info:gi});
                         }} />
                  <input placeholder="Relation" value={g.relation}
                         onChange={e=>{
                           const gi=[...addForm.guardians_info]; gi[idx]={...gi[idx], relation:e.target.value};
                           setAddForm({...addForm, guardians_info:gi});
                         }} />
                  <input placeholder="Contact (923…)" value={g.contact}
                         onChange={e=>{
                           const gi=[...addForm.guardians_info]; gi[idx]={...gi[idx], contact:digitsOnly(e.target.value)};
                           setAddForm({...addForm, guardians_info:gi});
                         }} />
                  <button className="action-btn red" onClick={()=>removeGuardianRow(idx)}>✕</button>
                </div>
              ))}
              <div className="modal-actions" style={{justifyContent:'flex-start'}}>
                <button className="action-btn" onClick={addGuardianRow} disabled={addForm.guardians_info.length>=5}>＋ Add Guardian</button>
              </div>

              <div className="modal-actions">
                <button className="action-btn" onClick={()=>setAddOpen(false)}>Cancel</button>
                <button className="action-btn blue" onClick={submitAdd}>Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit modal */}
        {edit && (
          <div className="modal-overlay">
            <div className="modal-card">
              <h3>Edit User</h3>
              <div className="form-grid">
                <label>
                  <span>Name</span>
                  <input value={edit.name} onChange={e=>setEdit({ ...edit, name:e.target.value })} />
                </label>
                <label>
                  <span>Email</span>
                  <input type="email" value={edit.email}
                         onChange={e=>setEdit({ ...edit, email:e.target.value })} />
                </label>
                <label>
                  <span>Role</span>
                  <select value={edit.role} onChange={e=>setEdit({ ...edit, role:e.target.value })}>
                    {ALLOWED_ROLES.map(r=> <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>

                {edit.role === 'student' && (
                  <>
                    <label>
                      <span>Student ID</span>
                      <input value={edit.studentId || ''}
                             onChange={e=>setEdit({ ...edit, studentId:e.target.value })} />
                    </label>
                    <label>
                      <span>Hostel Type</span>
                      <select value={edit.hostelType || 'hostler'}
                              onChange={e=>setEdit({ ...edit, hostelType:e.target.value })}>
                        {HOSTEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>Hostel</span>
                      <select value={edit.hostel || ''}
                              onChange={e=>setEdit({ ...edit, hostel:e.target.value })}>
                        <option value="">-- Select Hostel (Optional) --</option>
                        {hostels
                          .filter(h => h.hostelType === (edit.hostelType || 'hostler') && h.isActive)
                          .map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
                      </select>
                    </label>
                    <label style={{display:'flex',alignItems:'center',gap:8}}>
                      <input type="checkbox" checked={edit.affidavit || false}
                             onChange={e=>setEdit({ ...edit, affidavit:e.target.checked })} />
                      <span>Affidavit Submitted</span>
                    </label>
                    <label>
                      <span>Primary Guardian Contact (SMS)</span>
                      <input placeholder="923001234567" value={edit.guardian_contact || ''}
                             onChange={e=>setEdit({ ...edit, guardian_contact: digitsOnly(e.target.value) })} />
                    </label>
                  </>
                )}
              </div>

              <h4 style={{marginTop:10}}>Guardians</h4>
              {(edit.guardians_info||[{name:'',relation:'',contact:''}]).map((g,idx)=> (
                <div key={idx} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr auto',gap:8,marginTop:6}}>
                  <input placeholder="Name" value={g.name||''}
                         onChange={e=>{
                           const gi=[...(edit.guardians_info||[])]; gi[idx]={...gi[idx], name:e.target.value};
                           setEdit({...edit, guardians_info:gi});
                         }} />
                  <input placeholder="Relation" value={g.relation||''}
                         onChange={e=>{
                           const gi=[...(edit.guardians_info||[])]; gi[idx]={...gi[idx], relation:e.target.value};
                           setEdit({...edit, guardians_info:gi});
                         }} />
                  <input placeholder="Contact (923…)" value={g.contact||''}
                         onChange={e=>{
                           const gi=[...(edit.guardians_info||[])]; gi[idx]={...gi[idx], contact:digitsOnly(e.target.value)};
                           setEdit({...edit, guardians_info:gi});
                         }} />
                  <button className="action-btn red" onClick={()=>{
                    const gi=[...(edit.guardians_info||[])]; gi.splice(idx,1); setEdit({...edit, guardians_info:gi});
                  }}>✕</button>
                </div>
              ))}
              <div className="modal-actions" style={{justifyContent:'flex-start'}}>
                <button className="action-btn" onClick={()=>{
                  const gi=[...(edit.guardians_info||[])];
                  if (gi.length<5) gi.push({name:'',relation:'',contact:''});
                  setEdit({...edit, guardians_info:gi});
                }} disabled={(edit.guardians_info?.length||0)>=5}>＋ Add Guardian</button>
              </div>

              <div className="modal-actions">
                <button className="action-btn" onClick={()=>setEdit(null)}>Cancel</button>
                <button className="action-btn blue" onClick={saveEdit}>Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk modal (file upload) */}
        {bulkOpen && (
          <div className="modal-overlay">
            <div className="modal-card" style={{maxWidth:'900px', width:'min(90vw,900px)'}}>
              <h3>📤 Bulk Upload Students (CSV)</h3>
              <p style={{opacity:.8, margin:'6px 0 16px', fontSize: 14}}>
                Upload a CSV file to create multiple student accounts at once. 
                Set common values below, then upload your CSV with student details.
              </p>

              {/* Preset selections */}
              <div style={{
                background:'#f0f9ff', 
                border:'2px solid #3b82f6', 
                borderRadius:8, 
                padding:16, 
                marginBottom:16
              }}>
                <h4 style={{margin:'0 0 12px', color:'#1e40af', fontSize:16}}>
                  📋 Set Common Values for All Students
                </h4>
                
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                  <label style={{display:'flex', flexDirection:'column', gap:6}}>
                    <span style={{fontSize:13, fontWeight:600, color:'#374151'}}>
                      Student Type <span style={{color:'#dc2626'}}>*</span>
                    </span>
                    <select 
                      value={bulkPresets.hostelType} 
                      onChange={e => setBulkPresets({...bulkPresets, hostelType: e.target.value})}
                      style={{
                        padding:'10px 12px',
                        border:'1px solid #d1d5db',
                        borderRadius:6,
                        fontSize:14,
                        background:'#fff'
                      }}
                    >
                      <option value="hostler">Hostler</option>
                      <option value="non-hostler">Non-Hostler</option>
                    </select>
                  </label>

                  <label style={{display:'flex', flexDirection:'column', gap:6}}>
                    <span style={{fontSize:13, fontWeight:600, color:'#374151'}}>
                      Hostel (Optional)
                    </span>
                    <select 
                      value={bulkPresets.hostel} 
                      onChange={e => setBulkPresets({...bulkPresets, hostel: e.target.value})}
                      style={{
                        padding:'10px 12px',
                        border:'1px solid #d1d5db',
                        borderRadius:6,
                        fontSize:14,
                        background:'#fff'
                      }}
                    >
                      <option value="">-- No Hostel Assignment --</option>
                      {hostels
                        .filter(h => h.hostelType === bulkPresets.hostelType && h.isActive)
                        .map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
                    </select>
                  </label>
                </div>

                <div style={{
                  marginTop:12, 
                  padding:10, 
                  background:'#dbeafe', 
                  borderRadius:6,
                  fontSize:12,
                  color:'#1e40af'
                }}>
                  ℹ️ <b>Note:</b> These values will be applied to ALL students in the CSV file.
                </div>
              </div>

              {/* CSV Upload Section */}
              <div style={{
                background:'#fff',
                border:'1px solid #e5e7eb',
                borderRadius:8,
                padding:16
              }}>
                <h4 style={{margin:'0 0 12px', fontSize:15}}>📄 Upload CSV File</h4>
                
                <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:12}}>
                  <input 
                    type="file" 
                    accept=".csv,text/csv" 
                    onChange={handleFileChange}
                    style={{flex:1, minWidth:200}}
                  />
                  <button className="action-btn green" onClick={downloadTemplate}>
                    ⬇️ Download Template
                  </button>
                </div>

                <div style={{
                  background:'#f9fafb',
                  border:'1px solid #e5e7eb',
                  borderRadius:6,
                  padding:12,
                  fontSize:13,
                  color:'#6b7280'
                }}>
                  <b>CSV Requirements:</b>
                  <ul style={{margin:'8px 0 0', paddingLeft:20}}>
                    <li><b>Required:</b> name, email, studentId</li>
                    <li><b>Optional:</b> password (auto-generated if empty), affidavit (true/false), guardian_contact</li>
                    <li><b>Guardians:</b> Up to 5 guardians per student (guardian1_name, guardian1_relation, guardian1_contact, etc.)</li>
                  </ul>
                </div>

                {bulkFile && (
                  <div style={{background:'#f0fdf4', border:'1px solid #86efac', borderRadius:8, padding:12, marginTop:12}}>
                    <div style={{fontWeight:600, color:'#065f46'}}>✅ Selected: {bulkFile.name}</div>
                    {bulkInfo.rows > 0 && (
                      <div style={{marginTop:8, fontSize:14}}>
                        <div>📊 Total rows: <b>{bulkInfo.rows}</b></div>
                        <div style={{color:'#059669'}}>✅ Valid: <b>{bulkInfo.valid}</b></div>
                        {bulkInfo.invalid > 0 && <div style={{color:'#dc2626'}}>❌ Invalid: <b>{bulkInfo.invalid}</b></div>}
                        {bulkInfo.errors.length > 0 && (
                          <details style={{marginTop:8}}>
                            <summary style={{cursor:'pointer', fontWeight:600, color:'#dc2626'}}>
                              Show {bulkInfo.errors.length} error(s)
                            </summary>
                            <ul style={{marginTop:6, paddingLeft:20, fontSize:12}}>
                              {bulkInfo.errors.map((e,i)=> <li key={i} style={{fontFamily:'monospace', marginTop:4}}>{e}</li>)}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-actions" style={{marginTop:16}}>
                <button 
                  className="action-btn" 
                  onClick={()=>{ 
                    setBulkOpen(false); 
                    setBulkFile(null); 
                    setBulkInfo({rows:0,valid:0,invalid:0,errors:[]}); 
                    setBulkPresets({ hostelType: 'hostler', hostel: '' });
                  }}
                  disabled={bulkLoading}
                >
                  Cancel
                </button>
                <button 
                  className="action-btn blue" 
                  onClick={submitBulk}
                  disabled={!bulkFile || bulkLoading}
                  style={{
                    position:'relative',
                    minWidth:200,
                    opacity: (!bulkFile || bulkLoading) ? 0.6 : 1
                  }}
                >
                  {bulkLoading ? (
                    <>
                      <span style={{display:'inline-block', animation:'spin 1s linear infinite', marginRight:8}}>⏳</span>
                      Processing...
                    </>
                  ) : bulkFile ? (
                    '✅ Upload & Create Students'
                  ) : (
                    'Select CSV First'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Modal */}
        {uploadProgress.show && (
          <div className="modal-overlay" style={{zIndex:101}}>
            <div className="modal-card" style={{maxWidth:'450px', width:'min(90vw,450px)', textAlign:'center'}}>
              <div style={{padding:'30px 20px'}}>
                <div style={{fontSize:48, marginBottom:16}}>⏳</div>
                <h3 style={{margin:'0 0 8px', fontSize:20, color:'#1f2937'}}>Uploading Students...</h3>
                <p style={{margin:'0 0 20px', fontSize:14, color:'#6b7280'}}>
                  Please wait while we create the user accounts
                </p>
                
                {/* Progress Bar */}
                <div style={{background:'#e5e7eb', borderRadius:8, height:24, overflow:'hidden', marginBottom:12}}>
                  <div style={{
                    background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
                    height:'100%',
                    width: `${uploadProgress.percentage}%`,
                    transition: 'width 0.3s ease',
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    color:'#fff',
                    fontSize:12,
                    fontWeight:600
                  }}>
                    {uploadProgress.percentage}%
                  </div>
                </div>
                
                {/* Count Display */}
                <div style={{
                  display:'flex',
                  justifyContent:'center',
                  gap:20,
                  marginTop:20,
                  padding:'16px',
                  background:'#f9fafb',
                  borderRadius:8
                }}>
                  <div>
                    <div style={{fontSize:28, fontWeight:700, color:'#3b82f6'}}>
                      {uploadProgress.current}
                    </div>
                    <div style={{fontSize:12, color:'#6b7280', fontWeight:600}}>Processed</div>
                  </div>
                  <div style={{fontSize:28, color:'#d1d5db', alignSelf:'center'}}>/</div>
                  <div>
                    <div style={{fontSize:28, fontWeight:700, color:'#6b7280'}}>
                      {uploadProgress.total}
                    </div>
                    <div style={{fontSize:12, color:'#6b7280', fontWeight:600}}>Total</div>
                  </div>
                </div>
                
                <p style={{fontSize:12, color:'#9ca3af', marginTop:16, fontStyle:'italic'}}>
                  Emails will be sent in the background
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Result Modal */}
        {bulkResult && (
          <div className="modal-overlay" style={{zIndex:100}}>
            <div className="modal-card" style={{maxWidth:'600px', width:'min(90vw,600px)'}}>
              <div style={{
                textAlign:'center',
                padding:'20px 0',
                borderBottom: bulkResult.type === 'success' ? '3px solid #059669' : '3px solid #dc2626'
              }}>
                <div style={{
                  fontSize:60,
                  marginBottom:12
                }}>
                  {bulkResult.type === 'success' ? '✅' : '❌'}
                </div>
                <h3 style={{
                  margin:0,
                  fontSize:22,
                  color: bulkResult.type === 'success' ? '#059669' : '#dc2626'
                }}>
                  {bulkResult.type === 'success' ? 'Success!' : 'Error'}
                </h3>
              </div>

              <div style={{padding:'24px 20px'}}>
                <p style={{
                  fontSize:16,
                  color:'#374151',
                  margin:'0 0 20px',
                  textAlign:'center',
                  lineHeight:1.6
                }}>
                  {bulkResult.message}
                </p>

                {bulkResult.type === 'success' && bulkResult.details && (
                  <div style={{
                    background:'#f0fdf4',
                    border:'2px solid #86efac',
                    borderRadius:8,
                    padding:16,
                    marginBottom:16
                  }}>
                    {/* Bulk Upload Results */}
                    {(bulkResult.details.created !== undefined || bulkResult.details.skipped !== undefined) && (
                      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, textAlign:'center'}}>
                        <div>
                          <div style={{fontSize:32, fontWeight:700, color:'#059669'}}>
                            {bulkResult.details.created}
                          </div>
                          <div style={{fontSize:13, color:'#065f46', fontWeight:600}}>
                            Students Created
                          </div>
                        </div>
                        <div>
                          <div style={{fontSize:32, fontWeight:700, color:'#f59e0b'}}>
                            {bulkResult.details.skipped}
                          </div>
                          <div style={{fontSize:13, color:'#92400e', fontWeight:600}}>
                            Skipped (Duplicates)
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Bulk Delete Results */}
                    {bulkResult.details.deleted !== undefined && (
                      <div style={{textAlign:'center'}}>
                        <div style={{fontSize:48, fontWeight:700, color:'#059669'}}>
                          {bulkResult.details.deleted}
                        </div>
                        <div style={{fontSize:15, color:'#065f46', fontWeight:600}}>
                          User(s) Deleted Successfully
                        </div>
                        {bulkResult.details.failed > 0 && (
                          <div style={{marginTop:8, fontSize:13, color:'#dc2626'}}>
                            ({bulkResult.details.failed} failed)
                          </div>
                        )}
                      </div>
                    )}
                    
                    
                    {bulkResult.details.skippedList?.length > 0 && (
                      <details style={{marginTop:16}}>
                        <summary style={{cursor:'pointer', fontSize:13, fontWeight:600, color:'#92400e'}}>
                          View skipped emails
                        </summary>
                        <ul style={{marginTop:8, paddingLeft:20, fontSize:12, color:'#6b7280'}}>
                          {bulkResult.details.skippedList.map((item,i)=> (
                            <li key={i} style={{marginTop:4}}>
                              {typeof item === 'string' ? item : (
                                <span>
                                  <b>{item.email}</b> - <span style={{color:'#92400e'}}>{item.reason}</span>
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                {bulkResult.type === 'error' && bulkResult.details && (
                  <>
                    {bulkResult.details.missing && (
                      <div style={{
                        background:'#fef2f2',
                        border:'2px solid #fecaca',
                        borderRadius:8,
                        padding:16,
                        marginBottom:16
                      }}>
                        <div style={{fontWeight:600, color:'#991b1b', marginBottom:8}}>
                          Missing Required Columns:
                        </div>
                        <ul style={{margin:0, paddingLeft:20, color:'#dc2626'}}>
                          {bulkResult.details.missing.map((col,i)=> (
                            <li key={i}><code>{col}</code></li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {bulkResult.details.errors?.length > 0 && (
                      <details style={{
                        background:'#fef2f2',
                        border:'2px solid #fecaca',
                        borderRadius:8,
                        padding:16
                      }}>
                        <summary style={{cursor:'pointer', fontWeight:600, color:'#991b1b'}}>
                          View {bulkResult.details.errors.length} row error(s)
                        </summary>
                        <ul style={{marginTop:8, paddingLeft:20, fontSize:12, color:'#dc2626'}}>
                          {bulkResult.details.errors.map((err,i)=> (
                            <li key={i} style={{fontFamily:'monospace', marginTop:4}}>{err}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )}
              </div>

              <div className="modal-actions" style={{borderTop:'1px solid #e5e7eb', paddingTop:16}}>
                <button 
                  className="action-btn blue" 
                  onClick={()=>setBulkResult(null)}
                  style={{width:'100%', padding:'12px 24px', fontSize:15}}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* tiny CSS for modal (scoped) */}
        <style>{`
          .table-header-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:8px}
          .btn-row{display:flex;gap:8px;flex-wrap:wrap}
          .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;z-index:50}
          .modal-card{background:#fff;border-radius:10px;padding:20px;min-width:520px;max-width:90vw;box-shadow:0 8px 24px rgba(0,0,0,.15)}
          .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:12px 0}
          .form-grid label span{display:block;font-size:12px;opacity:.7;margin-bottom:4px}
          .form-grid input,.form-grid select{width:100%;padding:8px;border:1px solid #e5e7eb;border-radius:6px}
          .modal-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:12px}
          .action-btn{padding:6px 12px;border:none;border-radius:6px;background:#e5e7eb;cursor:pointer;transition:all 0.2s}
          .action-btn.blue{background:#2563eb;color:#fff}
          .action-btn.red{background:#dc2626;color:#fff}
          .action-btn.green{background:#059669;color:#fff}
          .action-btn:disabled{opacity:.6;cursor:not-allowed}
          .action-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 2px 8px rgba(0,0,0,0.15)}
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>

        {loading && <p style={{opacity:.7}}>Loading…</p>}
      </div>
    </div>
  );
}
